import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import MarketingShell from "@/components/marketing/MarketingShell";
import SEO from "@/components/marketing/SEO";

const FEATURES = [
  "Unlimited clients & team members",
  "GST returns tracker (GSTR-1, 3B, 9, etc.)",
  "Income Tax return tracker (ITR-1 to ITR-7)",
  "TDS return tracker (24Q, 26Q, 27Q)",
  "ROC compliance tracker (annual, event-based)",
  "Vyapar-style multi-line GST invoicing + PDF",
  "Branded invoice template with your firm logo",
  "Banks, expenses, cash & books module",
  "AI Assistant powered by GPT-5.2",
  "AI HSN/SAC finder with GST rate",
  "AI WhatsApp message drafting",
  "AI client insights + smart reminders",
  "WhatsApp Connect — chat-with-client",
  "Document vault — 50 MB per file",
  "Bulk client import (Excel/CSV)",
  "GST/IT/TRACES portal autofill launcher",
  "Granular per-user permissions (RBAC)",
  "Daily backups · TLS 1.3 encryption",
];

export default function Pricing() {
  return (
    <MarketingShell>
      <SEO
        title="Pricing — TAXINPHO · ₹999/month or ₹9,999/year for Indian CAs"
        description="Simple, transparent pricing for Indian CA offices. Start with a 7-day free trial, then ₹999/month or ₹9,999/year (save ~17%). All 18 features included — no per-user, no per-client fees."
        canonical="https://taxinpho.co/pricing"
      />
      <section className="max-w-7xl mx-auto px-5 py-16 md:py-24">
        <div className="max-w-3xl mb-12">
          <p className="text-xs font-bold uppercase tracking-widest text-[#002FA7] mb-3">Simple, transparent pricing</p>
          <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter mb-4">
            One price.<br />Every feature.
          </h1>
          <p className="text-zinc-600 text-lg leading-relaxed">
            No per-user fees, no per-client charges, no feature gating.
            Start with a 7-day free trial — switch to monthly or annual when you&apos;re ready.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-14">
          {/* Trial */}
          <div className="border-2 border-zinc-200 p-7">
            <div className="mb-5">
              <p className="font-display font-bold text-xl mb-1">Free Trial</p>
              <p className="text-zinc-600 text-sm">For new CA offices, no commitment</p>
            </div>
            <div className="mb-6">
              <p className="font-display font-black text-5xl tracking-tighter mb-1">₹0</p>
              <p className="text-sm text-zinc-500">for 7 days · then ₹999/mo or ₹9,999/yr</p>
            </div>
            <Link to="/register" data-testid="pricing-page-trial" className="block text-center bg-zinc-100 text-zinc-900 py-3 font-semibold hover:bg-zinc-200 mb-6">
              Start free trial
            </Link>
            <p className="text-xs text-zinc-500 mb-3">Includes:</p>
            <ul className="space-y-1.5 text-sm">
              <li className="flex items-start gap-2"><Check size={14} className="text-[#039855] mt-0.5" />All features unlocked</li>
              <li className="flex items-start gap-2"><Check size={14} className="text-[#039855] mt-0.5" />No credit card required</li>
              <li className="flex items-start gap-2"><Check size={14} className="text-[#039855] mt-0.5" />Cancel anytime</li>
            </ul>
          </div>

          {/* Monthly — popular */}
          <div className="border-2 border-[#002FA7] bg-[#EFF4FF] p-7 relative">
            <span className="absolute -top-3 left-6 bg-[#002FA7] text-white text-xs font-bold px-3 py-1">MOST POPULAR</span>
            <div className="mb-5">
              <p className="font-display font-bold text-xl mb-1">Monthly</p>
              <p className="text-zinc-600 text-sm">Pay as you go, cancel anytime</p>
            </div>
            <div className="mb-6">
              <p className="font-display font-black text-5xl tracking-tighter mb-1">
                ₹999<span className="text-2xl text-zinc-500">/mo</span>
              </p>
              <p className="text-sm text-zinc-500">+ 18% GST · billed monthly</p>
            </div>
            <Link to="/register" data-testid="pricing-page-monthly" className="block text-center bg-[#002FA7] text-white py-3 font-semibold hover:bg-blue-800 mb-6">
              Get started
            </Link>
            <p className="text-xs text-zinc-500 mb-3">Everything in Free Trial, plus:</p>
            <ul className="space-y-1.5 text-sm">
              <li className="flex items-start gap-2"><Check size={14} className="text-[#039855] mt-0.5" />Unlimited usage · no limits</li>
              <li className="flex items-start gap-2"><Check size={14} className="text-[#039855] mt-0.5" />Priority email support</li>
              <li className="flex items-start gap-2"><Check size={14} className="text-[#039855] mt-0.5" />Cancel anytime, no questions</li>
            </ul>
          </div>

          {/* Yearly */}
          <div className="border-2 border-zinc-900 p-7 bg-zinc-950 text-white relative">
            <span className="absolute -top-3 right-6 bg-[#1ABEDC] text-zinc-950 text-xs font-bold px-3 py-1">SAVE ~17%</span>
            <div className="mb-5">
              <p className="font-display font-bold text-xl mb-1">Yearly</p>
              <p className="text-zinc-400 text-sm">Best for established firms</p>
            </div>
            <div className="mb-6">
              <p className="font-display font-black text-5xl tracking-tighter mb-1">
                ₹9,999<span className="text-2xl text-zinc-500">/yr</span>
              </p>
              <p className="text-sm text-zinc-400">Equivalent to ₹833/month · save ₹1,989</p>
            </div>
            <Link to="/register" data-testid="pricing-page-yearly" className="block text-center bg-[#1ABEDC] text-zinc-950 py-3 font-semibold hover:bg-cyan-400 mb-6">
              Save with yearly
            </Link>
            <p className="text-xs text-zinc-500 mb-3">Everything in Monthly, plus:</p>
            <ul className="space-y-1.5 text-sm">
              <li className="flex items-start gap-2"><Check size={14} className="text-[#1ABEDC] mt-0.5" />~17% savings vs monthly</li>
              <li className="flex items-start gap-2"><Check size={14} className="text-[#1ABEDC] mt-0.5" />WhatsApp support</li>
              <li className="flex items-start gap-2"><Check size={14} className="text-[#1ABEDC] mt-0.5" />Refund within 7 days, no questions</li>
            </ul>
          </div>
        </div>

        {/* Features included */}
        <div className="border-t border-zinc-200 pt-12">
          <p className="text-xs font-bold uppercase tracking-widest text-[#002FA7] mb-3">Every plan includes</p>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mb-8">
            All 18 features. No upsell.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {FEATURES.map((f) => (
              <div key={f} className="flex items-start gap-2 text-sm">
                <Check size={14} className="text-[#039855] mt-1 flex-shrink-0" />
                <span>{f}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 bg-zinc-950 text-white p-10 sm:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div>
            <h3 className="font-display font-black text-3xl tracking-tighter mb-2">Ready to switch?</h3>
            <p className="text-zinc-400">Start your free trial today. No credit card. Money-back within 7 days of payment.</p>
          </div>
          <Link
            to="/register"
            data-testid="pricing-bottom-cta"
            className="bg-[#1ABEDC] text-zinc-950 px-7 py-3.5 font-semibold flex items-center gap-2 flex-shrink-0 hover:bg-cyan-400"
          >
            Start free trial <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </MarketingShell>
  );
}
